import { reactive, Variable, Transform, VMap } from 'alkali'
import { Workflow, Broadcast } from 'types/dtos'
//import app from 'app/app'
import * as _ from 'lodash'

export default class UserWork extends Transform.with({
	workflows: [Workflow],
	broadcasts: [Broadcast],
	virtualVariables: [],
}) {
	private resolveSSLoaded
	readonly studySetDataLoaded = new Promise(r => (this.resolveSSLoaded = r))

	transform() {
		return get('/portal/Work', {
			headers: {
				Subscribe: app.clientId,
			},
		}).then(work => {
			UserWork.dereferenceWork(work)
			if (app.user.getCurrentStudySet && app.user.getCurrentStudySet()) {
				// we have a study set, load studyset specific work
				return this.retrieveStudySetWork(work).then(work => {
					if (this.resolveSSLoaded) {
						this.resolveSSLoaded(work)
						this.resolveSSLoaded = null // resolve on initial load only
					}
					return work
				})
			}
			// otherwise we load that data later
			if (app.user._studySetVar)
				app.user._studySetVar.subscribe(event => {
					if (event.value()) this.retrieveStudySetWork(work).then(() => this.updated())
				})
			return work
		})
	}

	static dereferenceWork(work) {
		work.broadcasts = this.getValidBroadcasts(work.broadcasts)
		const scopes = _.keyBy(work.scopes, 'Id')
		for (const workflow of work.workflows) {
			workflow.Scopes = workflow.Scopes ? workflow.Scopes.map(scope => scopes[scope.Id]) : []
		}
		for (const broadcast of work.broadcasts) {
			broadcast.Scopes = broadcast.Scopes ? broadcast.Scopes.map(scope => scopes[scope.Id]) : []
		}
		for (const virtualVariable of work.virtualVariables) {
			virtualVariable.Scopes = virtualVariable.Scopes
				? virtualVariable.Scopes.map(scope => scopes[scope.Id])
				: []
		}
		return work
	}

	static getValidBroadcasts(broadcasts) {
		return _.chain(broadcasts)
			.compact()
			.filter(b => {
				return b.Status.Name !== 'Draft'
			})
			.value()
	}

	static workflowById(id) {
		const workflows = this.workflows.valueOf(true)
		if (workflows && workflows.then) {
			return workflows.then(workflows => workflows.find(workflow => workflow.Id == id))
		}
		return (workflows || []).find(workflow => workflow.Id == id)
	}

	retrieveStudySetWork(existingWork) {
		return get('/portal/Work/' + app.user.currentStudySet.Id, {
			headers: {
				Subscribe: app.clientId,
			},
		}).then(work => {
			UserWork.dereferenceWork(work)
			existingWork.workflows = _.unionBy(existingWork.workflows, work.workflows, 'Id')
			existingWork.broadcasts = _.unionBy(existingWork.broadcasts, work.broadcasts, 'Id')
			existingWork.virtualVariables = _.unionBy(
				existingWork.virtualVariables,
				work.virtualVariables,
				'Id'
			)
			insertVirtualVariables(existingWork.virtualVariables)
			return existingWork
		})
	}

	workflowsByType =
		this.workflows &&
		this.workflows.groupBy(workflow => {
			return workflow?.Type?.Name || 'Unknown'
		})

	static defaultInstance: UserWork
	addWorkflow(workflow: Workflow) {
		return post('/portal/Workflow', workflow).then(newWorkflow => {
			Object.assign(workflow, newWorkflow)
			this.workflows.push(workflow)
			return workflow
		})
	}
	updateWorkflow(workflow: Workflow) {
		return patch('/portal/Workflow/' + workflow.Id, workflow).then(workflow => {
			let i = 0
			for (const oldWorkflow of this.workflows) {
				if (oldWorkflow.Id == workflow.Id) {
					this.workflows.splice(i, 1, workflow)
					Object.assign(oldWorkflow, workflow) // copy over for any existing references
					return workflow
				}
				i++
			} // else just push it
			this.workflows.push(workflow)
			return workflow
		})
	}

	deleteWorkflow(workflow: Workflow) {
		workflow.Archived = true
		return this.updateWorkflow(workflow)
	}

	restoreWorkflow(workflow: Workflow) {
		workflow.Archived = false
		return this.updateWorkflow(workflow)
	}

	comment(comment, workflowId) {
		return post('/portal/WorkflowComment/', {
			data: {
				Text: comment.Text,
				Workflow: {
					Id: workflowId,
				},
				User: {
					Id: app.user.userId,
				},
			},
		}).then(comment => {
			this.workflows.updated()
			return comment
		})
	}

	taskUpdates(updates) {
		return post('/portal/WorkflowTask', {
			data: Object.assign(updates, { method: 'batch' }),
		}).then(response => {
			for (const updated of response) {
				const workflowId = updated?.Workflow?.Id
				if (workflowId) {
					Variable.from(UserWork.workflowById(workflowId)).updated()
				}
			}
			this.updated()
		})
	}

	updateComment(comment) {
		return patch('/portal/WorkflowComment/' + comment.Id, {
			data: {
				Text: comment.Text,
			},
		}).then(comment => {
			this.workflows.updated()
			return comment
		})
	}

	deleteComment(comment) {
		return deleteRequest('/portal/WorkflowComment/' + comment.Id).then(() =>
			this.workflows.updated()
		)
	}

	shareWorkflow(workflow, share) {
		share.Workflow = {
			Id: workflow.Id,
		}
		return post('/portal/Workflow/' + workflow.Id, {
			data: Object.assign(
				{
					method: 'shareWith',
				},
				share
			),
		}).then(updatedWorkflow => {
			const updatedScopes = updatedWorkflow.Scopes
			const newScopes = updatedScopes.filter(
				scope => !workflow.Scopes.some(existingScope => existingScope.Id == scope.Id)
			)
			share.Id = newScopes[0]?.Id
			workflow.Scopes.push(share)
			Variable.from(workflow).updated()
		})
	}
	unshareWorkflow(workflow, shareToRemove) {
		shareToRemove.Workflow = {
			Id: workflow.Id,
		}
		shareToRemove.method = 'unshare'
		return post('/portal/Workflow/' + workflow.Id, {
			data: shareToRemove,
		}).then(() => {
			workflow.Scopes = workflow.Scopes.filter(scope => scope.Id != shareToRemove.Scope.Id)
			Variable.from(workflow).updated()
		})
	}
}
const referencedWorkflows = new Map()
export const workflowById = id => UserWork.workflowById(id) || referencedWorkflows.get(id)
export const addReferencedWorkflow = workflow => referencedWorkflows.set(workflow.Id, workflow)

//import insertVirtualVariables from './assemble/InsertVirtualVariables'
import { get, post, patch, deleteRequest } from '../tools/request'
//import app from 'app/app'
